import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { LiaBell } from "react-icons/lia";
import { CheckCircle, XCircle, Check, FileText } from "lucide-react";
import logo from "../../assets/images/clientlogo.png";
import Menu from "./Menu";
import RequestIcon from "../../assets/icon/RequestIcon";
import axios from "axios";
import { BASEURL } from "../../constants";

const NavBar = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [lastReadAt, setLastReadAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const navigate = useNavigate();
  const notificationRef = useRef(null);
  const [showApplicationIcon, setShowApplicationIcon] = useState(false)

  const studentId = localStorage.getItem("studentId");
  // Check if a notification is unread
  const isUnread = (notification) => {
    if (!lastReadAt) return true;
    const notificationDate = new Date(notification.createdDate);
    const lastRead = new Date(lastReadAt);
    return notificationDate > lastRead;
  };

  // Get unread notifications
  const unreadNotifications = notifications.filter(isUnread);
  const hasUnreadNotifications = unreadNotifications.length > 0;

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${BASEURL}Student/ChangeStudentInformation/notification/${studentId}?viewInLms=true`
      );

      if (data?.success) {
        setNotifications(data.notifications || []);
        setLastReadAt(data.lastReadAt || null);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    if (notifications.length === 0) return;

    try {
      setMarkingRead(true);
      const studentId = localStorage.getItem("studentId");
      const { data } = await axios.post(
        `${BASEURL}Student/ChangeStudentInformation/notification/markAsRead?studentId=${studentId}`
      );

      if (data?.success) {
        // Update lastReadAt to current time so all notifications appear as read
        setLastReadAt(new Date().toISOString());
        setShowNotifications(false);
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    } finally {
      setMarkingRead(false);
    }
  };

  // const checkActiveApplication = async () => {
  //   try {
  //     const {data} = await axios.get(`${BASEURL}Student/HasApplications/${studentId}`)
  //     setShowApplicationIcon(data.hasApplications);
  //   } catch (error) {
  //     console.error("Error while checking active application")
  //   }
  // }

  // Handle notification click based on source
  const handleNotificationItemClick = (notification) => {
    setShowNotifications(false);

    switch (notification.notificationType) {
      case "ContactLog":
        navigate("/dashboard/alerts", {
          state: {
            selectedTab: "contactLog",
            highlightId: notification.notificationId,
          },
        });
        break;

      case "ChangeRequest":
        navigate("/dashboard/profile", {
          state: {
            showChangeRequestStatus: true,
            requestId: notification.notificationId,
            status: notification.status,
          },
        });
        break;

      default:
        navigate("/dashboard/alerts");
        break;
    }
  };

  // Get icon based on status
  const getNotificationIcon = (status) => {
    if (status === "Approved") {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    } else if (status === "Declined") {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  // Format relative date
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    if (!localStorage.getItem("studentId")) {
      navigate("/login");
    } else {
      fetchNotifications();
      //checkActiveApplication(); // Hide the Application Icon
    }
  }, [navigate]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  const optionSelection = (option) => {
    if (option === "request") {
      setIsOpen(false);
    }
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
    if (!showNotifications) {
      fetchNotifications();
    }
  };

  return (
    <header className="z-50 bg-white sm:px-10 h-22 my-1.5 shadow-md">
      <div className="flex justify-between p-2">
        <Link
          to="/dashboard"
          onClick={() => optionSelection("request")}
          className="h-12"
        >
          <img className="h-full w-full" src={logo} alt="Workflow logo" />
        </Link>

        <div
          className="flex items-center p-1 text-gray-400 rounded-md gap-4"
          aria-label="Alerts"
        >
          {/* <button
            type="button"
            title="Make a request"
            className="mr-1 cursor-pointer"
            onClick={() => navigate("/dashboard/makeARequest")}
          >
            <span className="w-[25px] h-[25px] flex justify-center items-center">
              <RequestIcon />
            </span>
          </button> */}
          
          {showApplicationIcon &&<button
            type="button"
            title="Applications"
            className="mr-1 cursor-pointer"
            onClick={() => navigate("/dashboard/applications")}
          >
            <span className="w-[25px] h-[25px] flex justify-center items-center">
              <FileText className="w-6 h-6" />
            </span>
          </button>}
          

          {/* Notification Bell */}
          <div className="relative" ref={notificationRef}>
            <button
              type="button"
              title="Alerts"
              className="mr-1 mt-2.5 cursor-pointer relative"
              onClick={handleNotificationClick}
            >
              <LiaBell className="block w-[25px] h-[25px]" />
              {/* Dot indicator for unread notifications */}
              {hasUnreadNotifications && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full h-2.5 w-2.5 animate-pulse" />
              )}
            </button>

            {/* Notification Popup */}
            {showNotifications && (
              <div className="fixed sm:absolute right-2 sm:right-0 top-16 sm:top-auto sm:mt-2 w-[calc(100vw-1rem)] sm:w-[22rem] max-w-[22rem] bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-base font-semibold text-gray-800">
                    Notifications
                  </h3>
                  {hasUnreadNotifications && (
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                      {unreadNotifications.length} new
                    </span>
                  )}
                </div>

                {/* Notification List */}
                <div className="max-h-96 overflow-y-auto notification-scroll">
                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-500">Loading...</p>
                    </div>
                  ) : notifications.length > 0 ? (
                    notifications.map((notification, index) => {
                      const unread = isUnread(notification);
                      return (
                        <div
                          key={`${notification.notificationType}-${notification.notificationId}-${index}`}
                          onClick={() => handleNotificationItemClick(notification)}
                          className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors relative ${
                            unread ? "bg-blue-100" : ""
                          }`}
                        >
                                                   
                          <div className={`${unread ? "" : ""}`}>
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex items-center gap-2">
                                <h4 className={`text-sm ${unread ? "font-semibold" : "font-medium"} text-gray-900`}>
                                  {notification.title}
                                </h4>
                                <span className="text-xs text-gray-400">
                                  {formatDate(notification.createdDate)}
                                </span>
                                {getNotificationIcon(notification.status)}
                              </div>
                              {/* Unread indicator dot */}
                              {unread && (
                                <span className="absolute right-6 top-6 -translate-y-1/2 h-2 w-2 bg-blue-500 rounded-full" />
                              )}
                            </div>

                            <p className="text-sm text-gray-600 line-clamp-2">
                              {notification.message.replace(/<br\s*\/?>/gi, "\n")}
                            </p>

                            {notification.createdBy && (
                              <p className="text-xs text-gray-400 mt-1">
                                by {notification.createdBy}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-8 text-center">
                      <LiaBell className="mx-auto text-4xl mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">No notifications</p>
                    </div>
                  )}
                </div>

                {/* Footer - Mark All as Read */}
                {hasUnreadNotifications && (
                  <div className="p-3 border-t border-gray-200">
                    <button
                      onClick={handleMarkAllAsRead}
                      disabled={markingRead}
                      className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {markingRead ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                          <span>Marking as read...</span>
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Mark all as read</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Menu isOpen={isOpen} props={props} handleClick={handleClick} />
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavBar;